import { addMoney } from '@shared/lib/money';
import { NASProductCategory, NASStatus } from '@websky/graphql';
import { useDispatch, useSelector } from 'react-redux';
import {
	getEveryPortSelectedServices,
	getEveryPortServicesByCategories
} from '../../../Checkout/store/everyPortServices/selectors';
import { SetEveryPortService, setEveryPortServiceAction } from '../../../Checkout/store/everyPortServices/actions';
import * as React from 'react';
import { Money } from '../../../types';
import { getOrder } from '../../../Checkout/store/order/selectors';
import { findMinPrice } from './utils';
import { getTravellerName } from './../../../utils';

export const useEveryPort = (type: NASProductCategory, segmentId: string, readonly: boolean = false) => {
	const dispatch = useDispatch();

	const servicesMap = useSelector(getEveryPortServicesByCategories),
		services = servicesMap.get(type) || [];

	const selectedServicesByCategory = useSelector(getEveryPortSelectedServices);
	const selectedServices = selectedServicesByCategory?.[type] ?? [];
	const order = useSelector(getOrder);

	const onServiceSave = (payload: SetEveryPortService[]) => {
		dispatch(
			setEveryPortServiceAction({
				category: type,
				services: [...payload]
			})
		);
	};

	const onRemoveService = (productId: string, travellerId: string, category = type) => {
		const selected = selectedServicesByCategory[category];

		dispatch(
			setEveryPortServiceAction({
				category,
				services: selected
					.map(selected => ({
						...selected,
						travellerIds: selected.travellerIds.filter(
							id => travellerId !== id || selected.product.id !== productId
						)
					}))
					.filter(selected => selected.travellerIds.length > 0)
			})
		);
	};

	const selectedSummary = React.useMemo(() => {
		const servicesTexts: {
				name: string;
				terminal: string;
			}[] = [],
			selectedPassengerNames: { name: string; status: NASStatus }[] = [];
		let totalPrice: Money = {
			amount: 0,
			currency: null
		};

		let detailsLink = '';
		let rules: { html?: string; lang?: string }[] = [];
		let modifyAvailable = !readonly;

		selectedServices.forEach(selectedService => {
			const service = services.find(service => service.id === selectedService.product.id);

			if (service && service.segmentId.toString() === segmentId) {
				servicesTexts.push({
					name: service.name,
					terminal: service.terminalName
				});

				if (selectedService.status !== NASStatus.New) {
					modifyAvailable = false;
				}

				selectedService.travellerIds.forEach(travellerId => {
					const travellerFare = service.fares.find(fare => fare.id === selectedService.fare.id),
						travellerPrice = travellerFare.prices.find(price => price.traveller.id === travellerId)?.price,
						traveller = order.travellers.find(traveller => traveller.id === travellerId),
						travellerStatus = selectedService.orders?.find(order => order.travellers.id === travellerId)
							?.status;

					selectedPassengerNames.push({
						name: getTravellerName(traveller),
						status: travellerStatus
					});

					if (travellerPrice) {
						totalPrice = addMoney(totalPrice, travellerPrice);
					}
				});
			}

			if (service?.terms?.[0]) {
				detailsLink = service.terms[0].url;

				rules = [...service.terms];
			}
		});

		return {
			texts: servicesTexts,
			passengerNames: selectedPassengerNames,
			price: totalPrice,
			detailsLink,
			modifyAvailable,
			rules
		};
	}, [selectedServices, segmentId, order.status]);

	const availableSegments = React.useMemo(() => {
		return order.flight.segments
			.filter(segment => services.some(service => service.segmentId.toString() === segment.segment.id))
			.map(segment => segment.segment);
	}, [order, services]);

	const priceFrom = React.useMemo(() => {
		return services
			.filter(service => service.segmentId.toString() === segmentId)
			.reduce((minPrice, service) => {
				const minServicePrice = findMinPrice(service, order.travellers);

				if (minServicePrice.amount < minPrice.amount) {
					return minServicePrice;
				}

				return minPrice;
			}, services[0]?.price);
	}, [services, segmentId]);

	return {
		services,
		onServiceSave,
		onRemoveService,
		selectedSummary,
		availableSegments,
		selectedServices,
		priceFrom
	};
};
